import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Anchor, Checkbox, Switch } from '@zillow/constellation';
import { Button, FormField, FormHelp, Heading, Input, Label, Select, Text } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';

import { RMWContext } from '../../providers/RMWContext';

import { useSelector } from '../../selectors/useSelector';

import { useAddressAutocomplete } from '../../hooks/queries/address';

import PostingActions from '../../actions/PostingActions';

import PropertyConstants from '../../constants/PropertyConstants';

import PostAListingSearchModuleCSEvents from '../../model/PostAListingSearchModuleCSEvents';

import Autocomplete from '../Autocomplete';

import { useLocationRestriction, useValidateForm } from './hooks';

// Styles
import * as S from './styles';
import { trackGtmEvent } from '../../../client/analytics';
import { GTM_EVENTS } from '@zg-rentals/google-tag-manager';

const {
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, TOWNHOUSE },
  LISTING_TYPES: { RENTAL, ROOM },
} = PropertyConstants;

const getUrlZRMLoginPageWithRedirectParam = (userFormSubQueryObj, currentPathname, paths) => {
  const urlSearchString = new URLSearchParams(userFormSubQueryObj).toString();
  const userFormSubQueryString = urlSearchString ? `?${urlSearchString}` : '';
  const zrmSignInPageQueryString = `${currentPathname}${userFormSubQueryString}`;

  return paths.zrmSignInPage(zrmSignInPageQueryString);
};

const getInitialPropertyType = (propertyType) => {
  return PROPERTY_CODES_BY_TYPE[propertyType] ? propertyType : '';
};

const PostingSearchModule = () => {
  // redux hooks
  const dispatch = useDispatch();

  const location = useLocation();

  const { paths } = useContext(RMWContext);

  const queryClient = useQueryClient();

  const urlQueryObject = parseQueryString(location.search);

  const { isLoggedIn, user } = useSelector(
    (state) => ({
      isLoggedIn: state.user.loggedIn || false,
      user: state.user,
    }),
    shallowEqual,
  );

  const [address, updateAddress] = useState(urlQueryObject?.address || '');
  const [errorObj, updateErrorObj] = useState({
    address: '',
    propertyType: '',
    numberOfUnits: '',
  });
  const [unit, updateUnit] = useState(urlQueryObject?.unit || '');
  const [numUnits, updateNumUnits] = useState(urlQueryObject?.numUnits || '');

  const [userSelectedPropertyType, updateUserSelectedPropertyType] = useState(
    useMemo(() => getInitialPropertyType(urlQueryObject?.propertyType), []),
  );

  const [isRoomForRent, updateIsRoomForRent] = useState(urlQueryObject?.isRoomForRent === 'true');
  const [duplicateAccountListingId, updateDuplicateAccountListingId] = useState('');
  const [isLoading, updateIsLoading] = useState(urlQueryObject?.submitted || false);

  // custom hooks
  const [setPartialAddress, queryData] = useAddressAutocomplete();
  const { checkLocationRestriction, propertyTypeFromApi } = useLocationRestriction({
    isLoggedIn,
    updateDuplicateAccountListingId,
    updateErrorObj,
    updateIsLoading,
  });
  const { checkIsFormComplete, verifyAddress, verifyNumUnits } = useValidateForm(updateErrorObj);

  let propertyType = userSelectedPropertyType || propertyTypeFromApi;
  if (propertyTypeFromApi === COMMUNITY) {
    propertyType = COMMUNITY;
  }
  const isCommunityType = propertyType === COMMUNITY;
  const isCondoType = propertyType === CONDO;

  useEffect(() => {
    if (urlQueryObject?.submitted) {
      createListing();
    } else {
      updateIsLoading(false);
    }
  }, [isLoggedIn]);

  // action handlers
  const handleAddressChange = (value) => {
    updateAddress(value);

    setPartialAddress(value);
  };

  const selectAddress = (selectedAddress) => {
    const { name } = selectedAddress;
    updateAddress(name);

    setPartialAddress(selectedAddress);

    checkLocationRestriction({
      address: name,
      unit,
      isRoomForRent,
      propertyType,
      numUnitsError: errorObj.numberOfUnits,
    });
  };

  const handlePropertyTypeSelect = (e) => {
    const selectedPropertyType = e.target.value;
    updateUserSelectedPropertyType(selectedPropertyType);

    checkLocationRestriction({
      address,
      unit,
      isRoomForRent,
      propertyType: selectedPropertyType,
      numUnitsError: errorObj.numberOfUnits,
    });
  };

  const handleUnitChange = (e) => {
    const enteredUnit = e.target.value;

    updateUnit(enteredUnit);
    verifyAddress({ address, unit: enteredUnit, errorObj });

    checkLocationRestriction({
      address,
      unit: enteredUnit,
      isRoomForRent,
      propertyType,
    });
  };

  const handleNumUnitsChange = (e) => {
    const enteredNumUnits = e.target.value;

    updateNumUnits(enteredNumUnits);

    verifyNumUnits({ numUnits: enteredNumUnits, errorObj });
  };

  const toggleSharedListing = () => {
    updateIsRoomForRent(!isRoomForRent);

    checkLocationRestriction({
      address,
      unit,
      isRoomForRent: !isRoomForRent,
      propertyType,
    });
  };

  const createListing = async () => {
    updateIsLoading(true);

    // Duplicate listing exists. Redirect to existing listing rather than create a new one
    if (duplicateAccountListingId && !isRoomForRent) {
      window.router.transitionTo(`${paths.propertiesPage}/${duplicateAccountListingId}/listing`);
      return;
    }

    const listingType = !isCommunityType && isRoomForRent ? ROOM : RENTAL;

    try {
      const currentProperty = await dispatch(
        PostingActions.property.create({
          listingType,
          propertyType: propertyType,
          fullAddress: address,
          ...(!isCommunityType && { unitNumber: unit }),
          ...(isCommunityType && { numUnits: numUnits }),
        }),
      );
      const { listingAlias } = currentProperty;

      updateIsLoading(false);
      PostAListingSearchModuleCSEvents.ListingCreated({ currentProperty, user });
      trackGtmEvent({ event: GTM_EVENTS.RENTAL_MANAGER_ADD_PROPERTY });

      queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

      const redirectUrl = `${paths.propertiesPage}/${listingAlias}/create-listing`;
      window.router.transitionTo(redirectUrl);
    } catch (err) {
      updateErrorObj({ ...errorObj, address: err.message });
      updateIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    updateIsLoading(true);

    const isFormComplete = checkIsFormComplete({
      address,
      propertyType,
      numUnits,
      errorObj,
    });

    PostAListingSearchModuleCSEvents.GetStarted({ user });

    if (!isFormComplete) {
      updateIsLoading(false);

      return;
    }

    const isValidAddress = await verifyAddress({ address, unit, errorObj });
    if (!isValidAddress) {
      updateIsLoading(false);

      return;
    }

    if (isLoggedIn) {
      createListing();
    } else {
      window.location.href = getUrlZRMLoginPageWithRedirectParam(
        { address, isRoomForRent, numUnits, propertyType, unit, submitted: true },
        location.pathname,
        paths,
      );
    }
  };

  return (
    <S.Container>
      <S.HeadingContainer>
        <Heading level={1} fontFamily="heading-alt" textStyle="heading-md" css={{ marginBottom: '200' }}>
          List your rental property for free — it&apos;s quick and easy
        </Heading>
        <Text asChild>
          <p>
            <strong>
              Fill your vacancy on the network with over 1 million daily visitors.
              <br />
              Post your rental today.
            </strong>
          </p>
        </Text>
      </S.HeadingContainer>

      <S.Row>
        <S.AddressInputContainer>
          <FormField error={!!errorObj.address}>
            <Label>Street address</Label>
            <Autocomplete
              items={queryData?.data?.addresses}
              onChange={handleAddressChange}
              onSelect={selectAddress}
              placeholder="Address"
              value={address}
            />
          </FormField>
          <FormHelp error={!!errorObj.address}>
            <div>{errorObj.address}</div>
          </FormHelp>
        </S.AddressInputContainer>

        {isCondoType && (
          <S.UnitInputContainer>
            <FormField>
              <Label>Unit</Label>
              <Input onChange={handleUnitChange} value={unit} />
            </FormField>
          </S.UnitInputContainer>
        )}

        {isCommunityType && (
          <S.UnitInputContainer>
            <FormField error={!!errorObj.numberOfUnits}>
              <Label>Number of units</Label>
              <Input onChange={handleNumUnitsChange} value={numUnits} />
              <FormHelp error={!!errorObj.numberOfUnits}>{errorObj.numberOfUnits}</FormHelp>
            </FormField>
          </S.UnitInputContainer>
        )}
      </S.Row>

      <S.PropertyTypeContainer>
        <FormField css={{ marginBlock: '300' }} error={!!errorObj.propertyType}>
          <Label>Property type</Label>
          <Select
            aria-label="Property type"
            value={propertyType || 'Property type'}
            onChange={handlePropertyTypeSelect}
            style={{ color: propertyType ? 'black' : '#A7A6AB' }}
            css={{ whiteSpace: 'pre' }}
          >
            <option disabled={true}>Property type</option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={HOUSE}>
              {HOUSE}
            </option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={CONDO}>
              {CONDO}
            </option>
            <option disabled={propertyTypeFromApi === COMMUNITY} value={TOWNHOUSE}>
              {TOWNHOUSE}
            </option>
            <option value={COMMUNITY}>{COMMUNITY}</option>
          </Select>
          <FormHelp error={!!errorObj.propertyType}>{errorObj.propertyType}</FormHelp>
        </FormField>
      </S.PropertyTypeContainer>

      {!isCommunityType && (
        <S.SharedPropertyContainer>
          <Text css={{ marginRight: '400' }}>This is a room for rent with shared living space</Text>
          <Switch
            size="md"
            checkbox={<Checkbox aria-label="Is room for rent" checked={isRoomForRent} onChange={() => {}} />}
            onClick={toggleSharedListing}
          />
        </S.SharedPropertyContainer>
      )}

      <Button loading={isLoading} tone="brand" emphasis="filled" onClick={handleSubmit}>
        Get started
      </Button>

      {!isLoggedIn && (
        <Text textStyle="body-sm" asChild css={{ marginTop: '400' }}>
          <p>
            Already have an account?{' '}
            <Anchor
              href={getUrlZRMLoginPageWithRedirectParam(
                { address, isRoomForRent, numUnits, propertyType, unit },
                location.pathname,
                paths,
              )}
            >
              Sign in
            </Anchor>
          </p>
        </Text>
      )}
    </S.Container>
  );
};

export default PostingSearchModule;
